import { useAui } from "@assistant-ui/react";
import { useCallback, useMemo, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { SkillCatalogIcon } from "@/components/skills/SkillCatalogIcon";
import { TabbioVerifiedBadge } from "@/components/skills/TabbioVerifiedBadge";
import { useAllVisibleSkills } from "@/components/skills/use-skills-query";
import {
  formatSkillCount,
  formatSkillCommand,
} from "@/components/skills/skill-name";
import type { ComposerMenuRow } from "./composer-menu.web";

/**
 * The composer's "/" commands: every skill this account can run, filtered by
 * what follows the slash. The rows render in the composer's shared menu
 * (`ComposerMenu`), the same list the "@" sources use, so both share the
 * gliding highlight, the keyboard and the footer.
 *
 * `query` is undefined while the draft is not a command, which also keeps the
 * skills request from running for every keystroke of an ordinary message.
 */
export function useSkillCommandRows(query: string | undefined) {
  const { t } = useTranslation("common");
  const aui = useAui();
  const skillsQuery = useAllVisibleSkills(query !== undefined);

  const visible = useMemo(
    () => skillsQuery.data?.skills ?? [],
    [skillsQuery.data?.skills],
  );

  const skills = useMemo(
    () =>
      visible.filter(
        (skill) =>
          !query ||
          skill.skillId.includes(query) ||
          skill.name.toLowerCase().includes(query) ||
          skill.nameAr?.toLowerCase().includes(query) ||
          skill.description.toLowerCase().includes(query) ||
          skill.descriptionAr?.toLowerCase().includes(query),
      ),
    [query, visible],
  );

  const rows = useMemo<ComposerMenuRow[]>(
    () =>
      skills.map((skill) => {
        const author = skill.authorName || t("skills.byTabbioName");
        const meta = `${t("skills.by", { author })} · ${t("skills.usageCount", {
          value: formatSkillCount(skill.usageCount, true),
        })}`;
        return {
          key: skill.id,
          leading: <SkillCatalogIcon skill={skill} size={24} />,
          title: formatSkillCommand(skill.skillId),
          badge: skill.verified ? <TabbioVerifiedBadge compact /> : null,
          description: skill.description,
          tooltip: meta,
        };
      }),
    [skills, t],
  );

  const select = useCallback(
    (index: number) => {
      const skill = skills[index];
      if (!skill) return;
      aui.composer().setText(`${formatSkillCommand(skill.skillId)} `);
    },
    [aui, skills],
  );

  return { rows, select, loading: skillsQuery.isLoading };
}

/** The commands list scrolls inside the menu rather than covering the page. */
export const SKILL_COMMAND_LIST_STYLE: CSSProperties = {
  maxHeight: 376,
  overflowY: "auto",
  overscrollBehavior: "contain",
};
